import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, User, Building, FileText, Check, Eye, EyeOff, Sparkles, Shield, Award, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import FileUpload from '@/components/ui/FileUpload';
import { SearchableMultiSelect } from '@/components/forms/SearchableMultiSelect';
import { useLanguage } from '@/contexts/LanguageContext';
import { useRegister } from '@/hooks/api/use-auth';
import { usePublicSpecializations } from '@/hooks/api/use-public';
import { useSiteSettings } from '@/hooks/api/use-site-settings';
import { cn } from '@/lib/utils';
import { getImageUrl } from '@/lib/image-utils';

interface RegistrationData {
  // Step 1: Basic Info
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  phone: string;
  type: 'admin' | 'client';
  
  // Step 2: Company Profile
  company_name: string;
  address: string;
  description: string;
  license_number: string;
  tax_id: string;
  profile_image: File | null;
  specialization_ids: number[];
  
  // Step 3: Documents
  commercial_register: File | null;
  tax_certificate: File | null;
  iban_certificate: File | null;
  zakat_income_certificate: File | null;
  social_insurance_certificate: File | null;
  company_profile: File | null;
}

const RegistrationWizard: React.FC = () => {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const registerMutation = useRegister();
  const { data: specializationsData, isLoading: specializationsLoading } = usePublicSpecializations();
  const { data: siteSettings, isLoading: isLoadingSettings } = useSiteSettings();
  const ArrowIcon = isRTL ? ArrowRight : ArrowLeft;
  
  const logoSrc = useMemo(() => {
    return getImageUrl(siteSettings?.data?.logo);
  }, [siteSettings?.data?.logo]);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [formData, setFormData] = useState<RegistrationData>({
    // Step 1
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    phone: '',
    type: 'client',
    
    // Step 2
    company_name: '',
    address: '',
    description: '',
    license_number: '',
    tax_id: '',
    profile_image: null,
    specialization_ids: [],
    
    // Step 3
    commercial_register: null,
    tax_certificate: null,
    iban_certificate: null,
    zakat_income_certificate: null,
    social_insurance_certificate: null,
    company_profile: null,
  });

  const totalSteps = 3;
  const progress = ((currentStep - 1) / totalSteps) * 100;

  const updateFormData = (field: keyof RegistrationData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user updates it
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = t('validation.required') || 'هذا الحقل مطلوب';
      if (!formData.email.trim()) newErrors.email = t('validation.required') || 'هذا الحقل مطلوب';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = t('validation.email') || 'البريد الإلكتروني غير صحيح';
      }
      if (!formData.password) newErrors.password = t('validation.required') || 'هذا الحقل مطلوب';
      else if (formData.password.length < 8) {
        newErrors.password = t('validation.passwordMin') || 'كلمة المرور يجب أن تكون 8 أحرف على الأقل';
      }
      if (!formData.password_confirmation) {
        newErrors.password_confirmation = t('validation.required') || 'هذا الحقل مطلوب';
      } else if (formData.password !== formData.password_confirmation) {
        newErrors.password_confirmation = t('validation.passwordMatch') || 'كلمات المرور غير متطابقة';
      }
      if (!formData.phone.trim()) newErrors.phone = t('validation.required') || 'هذا الحقل مطلوب';
    }

    if (step === 2) {
      if (!formData.company_name.trim()) newErrors.company_name = t('validation.required') || 'هذا الحقل مطلوب';
      if (!formData.address.trim()) newErrors.address = t('validation.required') || 'هذا الحقل مطلوب';
      if (!formData.description.trim()) newErrors.description = t('validation.required') || 'هذا الحقل مطلوب';
      if (formData.specialization_ids.length === 0) {
        newErrors.specialization_ids = t('validation.specializationRequired') || 'يجب اختيار تخصص واحد على الأقل';
      }
    }

    if (step === 3) {
      const requiredDocuments = [
        'commercial_register',
        'tax_certificate',
        'iban_certificate',
        'zakat_income_certificate',
        'company_profile',
        // 'social_insurance_certificate' (Content Certificate) is now optional
      ] as const;

      requiredDocuments.forEach(doc => {
        if (!formData[doc]) {
          newErrors[doc] = t('validation.documentRequired') || 'هذا المستند مطلوب';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    try {
      console.log('Form data before submit:', formData);
      
      // Prepare data object for authService (not FormData)
      const submitData: any = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        type: formData.type,
        phone: formData.phone,
        company_name: formData.company_name,
        address: formData.address,
        description: formData.description,
        license_number: formData.license_number,
        tax_id: formData.tax_id,
        profile_image: formData.profile_image,
        commercial_register: formData.commercial_register,
        tax_certificate: formData.tax_certificate,
        iban_certificate: formData.iban_certificate,
        zakat_income_certificate: formData.zakat_income_certificate,
        social_insurance_certificate: formData.social_insurance_certificate,
        company_profile: formData.company_profile,
      };

      // Handle specializations array
      if (formData.specialization_ids && formData.specialization_ids.length > 0) {
        submitData.specialization_ids = formData.specialization_ids;
      }

      console.log('Submit data object:', submitData);

      await registerMutation.mutateAsync(submitData);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Registration failed:', error);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Card className="bg-white border-slate-200 shadow-2xl hover:shadow-3xl transition-all duration-300 group">
            <CardHeader className="pb-6 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
              <CardTitle className="flex items-center gap-3 text-slate-900 text-xl">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <User className="h-5 w-5 text-primary" />
                </div>
                {t('auth.basicInfo') || 'المعلومات الأساسية'}
              </CardTitle>
              <p className="text-slate-600 text-sm">
                {t('auth.basicInfoDesc') || 'أدخل معلوماتك الأساسية لإنشاء حساب'}
              </p>
            </CardHeader>
            <CardContent className="space-y-6 p-6">
              <div className="space-y-2">
                <Input
                  placeholder={t('auth.fullName') || 'الاسم الكامل'}
                  value={formData.name}
                  onChange={(e) => updateFormData('name', e.target.value)}
                  className={cn(
                    "bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300",
                    errors.name && "border-red-400 focus:border-red-400"
                  )}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                  {errors.name}
                  
                </p>}
              </div>

              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder={t('auth.email') || 'البريد الإلكتروني'}
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  className={cn(
                    "bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300",
                    errors.email && "border-red-400 focus:border-red-400"
                  )}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                  {errors.email}
                </p>}
              </div>

              <div className="space-y-2">
                <Input
                  placeholder={t('auth.phone') || 'رقم الهاتف'}
                  value={formData.phone}
                  onChange={(e) => updateFormData('phone', e.target.value)}
                  className={cn(
                    "bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300",
                    errors.phone && "border-red-400 focus:border-red-400"
                  )}
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                  {errors.phone}
                </p>}
              </div>

              <div className="space-y-2">
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder={t('auth.password') || 'كلمة المرور'}
                    value={formData.password}
                    onChange={(e) => updateFormData('password', e.target.value)}
                    className={cn(
                      `${errors.password ? "border-red-400 focus:border-red-400" : "border-slate-200 focus:border-primary"} bg-white text-slate-900 placeholder-slate-400 focus:ring-primary transition-all duration-300`,
                      isRTL ? "pr-10" : "pl-10"
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors`}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                  {errors.password}
                </p>}
              </div>

              <div className="space-y-2">
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder={t('auth.confirmPassword') || 'تأكيد كلمة المرور'}
                    value={formData.password_confirmation}
                    onChange={(e) => updateFormData('password_confirmation', e.target.value)}
                    className={cn(
                      `${errors.password_confirmation ? "border-red-400 focus:border-red-400" : "border-slate-200 focus:border-primary"} bg-white text-slate-900 placeholder-slate-400 focus:ring-primary transition-all duration-300`,
                      isRTL ? "pr-10" : "pl-10"
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors`}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password_confirmation && <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                  {errors.password_confirmation}
                </p>}
              </div>
            </CardContent>
          </Card>
        );

      case 2:
        return (
          <Card className="bg-white border-slate-200 shadow-2xl hover:shadow-3xl transition-all duration-300 group">
            <CardHeader className="pb-6 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
              <CardTitle className="flex items-center gap-3 text-slate-900 text-xl">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Building className="h-5 w-5 text-primary" />
                </div>
                {t('auth.companyInfo') || 'معلومات الشركة'}
              </CardTitle>
              <p className="text-slate-600 text-sm">
                {t('auth.companyInfoDesc') || 'أدخل معلومات شركتك وتخصصاتك'}
              </p>
            </CardHeader>
            <CardContent className="space-y-6 p-6">
              <div className="space-y-2">
                <Input
                  placeholder={t('profile.companyName') || 'اسم الشركة'}
                  value={formData.company_name}
                  onChange={(e) => updateFormData('company_name', e.target.value)}
                  className={cn(
                    "bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300",
                    errors.company_name && "border-red-400 focus:border-red-400"
                  )}
                />
                {errors.company_name && <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                  {errors.company_name}
                </p>}
              </div>

              <div className="space-y-2">
                <Input
                  placeholder={t('profile.address') || 'العنوان'}
                  value={formData.address}
                  onChange={(e) => updateFormData('address', e.target.value)}
                  className={cn(
                    "bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300",
                    errors.address && "border-red-400 focus:border-red-400"
                  )}
                />
                {errors.address && <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                  {errors.address}
                </p>}
              </div>

              <div className="space-y-2">
                <Textarea
                  placeholder={t('profile.companyDescription') || 'وصف الشركة'}
                  value={formData.description}
                  onChange={(e) => updateFormData('description', e.target.value)}
                  rows={4}
                  className={cn(
                    "bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300 resize-none",
                    errors.description && "border-red-400 focus:border-red-400"
                  )}
                />
                {errors.description && <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                  {errors.description}
                </p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Input
                    placeholder={t('profile.licenseNumber') || 'رقم الرخصة'}
                    value={formData.license_number}
                    onChange={(e) => updateFormData('license_number', e.target.value)}
                    className="bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300"
                  />
                </div>
                <div>
                  <Input
                    placeholder={t('profile.taxId') || 'الرقم الضريبي'}
                    value={formData.tax_id}
                    onChange={(e) => updateFormData('tax_id', e.target.value)}
                    className="bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300"
                  />
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <FileUpload
                  onFileSelect={(file) => updateFormData('profile_image', file)}
                  onFileRemove={() => updateFormData('profile_image', null)}
                  selectedFile={formData.profile_image}
                  accept="image/*"
                  maxSize={2 * 1024 * 1024} // 2MB
                  label={t('profile.profileImage') || 'صورة الملف الشخصي'}
                  description={t('profile.profileImageDesc') || 'صورة شخصية أو شعار الشركة (اختياري)'}
                  required={false}
                />
              </div>

              {/* Specializations */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase size={16} className="text-primary" />
                  <h4 className="text-sm font-medium text-slate-900">
                    {t('profile.specializations') || 'التخصصات'} *
                  </h4>
                </div>
                {specializationsLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-10 bg-slate-200 rounded animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <SearchableMultiSelect
                    options={specializationsData?.data || []}
                    selectedIds={formData.specialization_ids}
                    onChange={(selectedIds) => updateFormData('specialization_ids', selectedIds)}
                    placeholder={t('profile.selectSpecializations') || 'اختر التخصصات...'}
                    searchPlaceholder={t('profile.searchSpecializations') || 'ابحث عن التخصصات...'}
                    noOptionsText={t('profile.noSpecializationsFound') || 'لم يتم العثور على تخصصات'}
                    maxVisible={3}
                  />
                )}
                {errors.specialization_ids && <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                  {errors.specialization_ids}
                </p>}
              </div>
            </CardContent>
          </Card>
        );

      case 3:
        return (
          <Card className="bg-white border-slate-200 shadow-2xl hover:shadow-3xl transition-all duration-300 group">
            <CardHeader className="pb-6 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
              <CardTitle className="flex items-center gap-3 text-slate-900 text-xl">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                {t('auth.requiredDocuments') || 'المستندات المطلوبة'}
              </CardTitle>
              <p className="text-slate-600 text-sm">
                {t('auth.documentsDesc') || 'يرجى رفع جميع المستندات المطلوبة لإكمال التسجيل'}
              </p>
            </CardHeader>
            <CardContent className="space-y-6 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <FileUpload
                    onFileSelect={(file) => updateFormData('commercial_register', file)}
                    onFileRemove={() => updateFormData('commercial_register', null)}
                    selectedFile={formData.commercial_register}
                    accept=".pdf,.doc,.docx"
                    maxSize={10 * 1024 * 1024}
                    label={t('documents.commercialRegister') || 'السجل التجاري'}
                    description={t('documents.commercialRegisterDesc') || 'سجل الشركة التجاري ساري المفعول'}
                    required={true}
                    error={errors.commercial_register}
                  />
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <FileUpload
                    onFileSelect={(file) => updateFormData('tax_certificate', file)}
                    onFileRemove={() => updateFormData('tax_certificate', null)}
                    selectedFile={formData.tax_certificate}
                    accept=".pdf,.doc,.docx"
                    maxSize={10 * 1024 * 1024}
                    label={t('documents.taxCertificate') || 'شهادة الضريبة'}
                    description={t('documents.taxCertificateDesc') || 'شهادة الضريبة المضافة'}
                    required={true}
                    error={errors.tax_certificate}
                  />
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <FileUpload
                    onFileSelect={(file) => updateFormData('iban_certificate', file)}
                    onFileRemove={() => updateFormData('iban_certificate', null)}
                    selectedFile={formData.iban_certificate}
                    accept=".pdf,.doc,.docx"
                    maxSize={10 * 1024 * 1024}
                    label={t('documents.ibanCertificate') || 'شهادة IBAN'}
                    description={t('documents.ibanCertificateDesc') || 'شهادة حساب البنك الدولي'}
                    required={true}
                    error={errors.iban_certificate}
                  />
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <FileUpload
                    onFileSelect={(file) => updateFormData('zakat_income_certificate', file)}
                    onFileRemove={() => updateFormData('zakat_income_certificate', null)}
                    selectedFile={formData.zakat_income_certificate}
                    accept=".pdf,.doc,.docx"
                    maxSize={10 * 1024 * 1024}
                    label={t('documents.zakatCertificate') || 'شهادة الزكاة والدخل'}
                    description={t('documents.zakatCertificateDesc') || 'شهادة سداد الزكاة والدخل'}
                    required={true}
                    error={errors.zakat_income_certificate}
                  />
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <FileUpload
                    onFileSelect={(file) => updateFormData('social_insurance_certificate', file)}
                    onFileRemove={() => updateFormData('social_insurance_certificate', null)}
                    selectedFile={formData.social_insurance_certificate}
                    accept=".pdf,.doc,.docx"
                    maxSize={10 * 1024 * 1024}
                    label={t('documents.socialInsurance') || 'شهادة المحتوى (التأمينات الاجتماعية)'}
                    description={t('documents.socialInsuranceDesc') || 'شهادة المحتوى للمحتوى الرقمي (اختياري)'}
                    required={false}
                    error={errors.social_insurance_certificate}
                  />
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <FileUpload
                    onFileSelect={(file) => updateFormData('company_profile', file)}
                    onFileRemove={() => updateFormData('company_profile', null)}
                    selectedFile={formData.company_profile}
                    accept=".pdf,.doc,.docx"
                    maxSize={10 * 1024 * 1024}
                    label={t('documents.companyProfile') || 'ملف الشركة التعريفي'}
                    description={t('documents.companyProfileDesc') || 'ملف تعريفي عن الشركة وخدماتها'}
                    required={true}
                    error={errors.company_profile}
                  />
                </div>
              </div>

              {/* Security Notice */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 flex items-start gap-3">
                <Shield size={20} className="text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="text-slate-900 font-medium mb-1">
                    {t('auth.securityNotice') || 'ملاحظة أمان'}
                  </h4>
                  <p className="text-slate-600 text-sm">
                    {t('auth.securityNoticeDesc') || 'جميع المستندات يتم تشفيرها وتخزينها بأمان. لا نشارك معلوماتك مع أي طرف ثالث.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative overflow-hidden">
      {/* Subtle Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary/3 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/2 rounded-full blur-2xl animate-pulse delay-500"></div>
      </div>
      
      {/* Decorative Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      />
      
      <div className="relative z-10 min-h-screen flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-5xl">
          {/* Enhanced Logo and Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-2xl shadow-2xl mb-6 group hover:scale-105 transition-transform duration-300 border border-slate-200">
              <Link to="/" className="flex items-center justify-center w-full h-full">
                {isLoadingSettings || !logoSrc ? (
                  <div className="h-16 w-16 animate-pulse bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%] rounded-lg" 
                       style={{ animation: 'shimmer 1.5s infinite' }} />
                ) : (
                  <img src={logoSrc} alt="KYNZ" className="h-16 w-auto" />
                )}
              </Link>
            </div>
            <div className="space-y-4">
              <h1 className="text-5xl font-bold text-slate-900 tracking-tight">
                {t('auth.createAccount') || 'إنشاء حساب جديد'}
              </h1>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                {t('auth.joinPlatform') || 'انضم إلى منصتنا وابدأ رفع المناقصات'}
              </p>
              <div className="flex items-center justify-center gap-4 mt-6">
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 shadow-sm">
                  <Shield size={16} className="text-primary" />
                  <span className="text-slate-700 text-sm font-medium">{t('auth.secure100')}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 shadow-sm">
                  <Sparkles size={16} className="text-primary" />
                  <span className="text-slate-700 text-sm font-medium">{t('auth.easyToUse')}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 shadow-sm">
                  <Award size={16} className="text-primary" />
                  <span className="text-slate-700 text-sm font-medium">{t('auth.realOpportunities')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 bg-primary text-white rounded-full">
                  <span className="font-semibold text-sm">{currentStep}</span>
                </div>
                <span className="text-slate-700 font-medium">
                  {t('auth.step') || `الخطوة ${currentStep} من ${totalSteps}`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-700 font-medium">
                 {t('auth.completed') || 'مكتمل'} 
                </span>
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center border border-primary/20">
                  <span className="text-primary font-bold text-lg">{Math.round(progress)}%</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <Progress value={progress} className="h-3 bg-slate-200" />
              <div className="absolute top-0 left-0 h-3 bg-gradient-to-r from-primary to-primary/80 rounded-full transition-all duration-500" style={{ width: `${progress}%` }}></div>
            </div>
          </div>

        {/* Enhanced Form Content */}
          <div className="mb-8">
            {renderStep()}
          </div>

          {/* Enhanced Navigation Buttons */}
          <div className="flex items-center justify-between bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 1 || registerMutation.isPending}
              className="flex items-center gap-2 bg-white border-slate-300 text-slate-700 hover:bg-slate-50 transition-all duration-300"
            >
              <ArrowIcon size={16} />
              {t('auth.previous') || 'السابق'}
            </Button>

            <div className="flex gap-3">
              <Link to="/">
                <Button 
                  variant="ghost" 
                  disabled={registerMutation.isPending}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 transition-all duration-300"
                >
                  {t('auth.cancel') || 'إلغاء'}
                </Button>
              </Link>

              {currentStep === totalSteps ? (
                <Button
                  onClick={handleSubmit}
                  disabled={registerMutation.isPending}
                  className="flex items-center gap-2 bg-primary text-white hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {registerMutation.isPending ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      {t('auth.registering') || 'جاري التسجيل...'}
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      {t('auth.completeRegistration') || 'إكمال التسجيل'}
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  className="flex items-center gap-2 bg-primary text-white hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {t('auth.next') || 'التالي'}
                  <ArrowIcon size={16} />
                </Button>
              )}
            </div>
          </div>

          {/* Enhanced Login Link */}
          <div className="text-center mt-8">
            <p className="text-slate-600">
              {t('auth.haveAccount') || 'لديك حساب بالفعل؟'}{' '}
              <Link 
                to="/auth" 
                className="text-primary font-semibold hover:underline underline-offset-2 transition-all duration-300"
              >
                {t('auth.login') || 'تسجيل الدخول'}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationWizard;
